import { Component, inject, OnInit } from '@angular/core';
import { TransactionHistory } from '../../models/subscription';
import { SubscriptionService } from '../../services/subscription.service';
import { takeUntil } from 'rxjs';
import { MenuComponentBase } from '../../../../shared/base-classes/menu-component-base';
import { CommonModule, DatePipe } from '@angular/common';
import { PageSizeComponent } from '../../../../shared/components/page-size/page-size.component';
import { SvgIconComponent } from '../../../../shared/components/svg-icon/svg-icon.component';
import {
  TableColumn,
  TableComponent,
} from '../../../../shared/components/table/table.component';
import { Router } from '@angular/router';

@Component({
  selector: 'app-transaction-history',
  templateUrl: './transaction-history.component.html',
  styleUrls: ['./transaction-history.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    TableComponent,
    PageSizeComponent,
    SvgIconComponent,
  ],
})
export class TransactionHistoryComponent
  extends MenuComponentBase
  implements OnInit
{
  private subscriptionService = inject(SubscriptionService);
  private router = inject(Router);

  transactions: TransactionHistory[] = [];
  transactionsLoading = false;

  tableColumns: TableColumn[] = [
    { header: 'Payment Date', width: '20%' },
    { header: 'Invoice', width: '20%' },
    { header: 'Amount', width: '15%' },
    { header: 'Status', width: '15%' },
    { header: '', width: '15%' },
    { header: '', width: '15%' },
  ];

  pageSize = 50;
  hasNext = false;
  hasPrevious = false;
  nextCursor?: string;
  previousCursor?: string;

  override ngOnInit() {
    super.ngOnInit();
    this.loadTransactions();
  }

  loadTransactions(startingAfter?: string, endingBefore?: string): void {
    this.transactionsLoading = true;
    this.subscriptionService
      .getTransactionHistory(this.pageSize, startingAfter, endingBefore)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.transactions = res.results;
          this.hasNext = !!res.next;
          this.hasPrevious = !!res.previous;
          this.nextCursor = this.transactions.at(-1)?.invoice_id;
          this.previousCursor = this.transactions[0]?.invoice_id;
          this.transactionsLoading = false;
        },
        error: () => {
          this.transactionsLoading = false;
        },
      });
  }

  getStatusClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'paid':
        return 'success';

      case 'open':
        return 'pending';

      case 'void':
      case 'uncollectible':
      case 'draft':
        return 'failed';

      default:
        return 'default';
    }
  }

  onNext(): void {
    if (!this.hasNext || !this.nextCursor) {
      return;
    }
    this.loadTransactions(this.nextCursor);
  }

  onPrevious(): void {
    if (!this.hasPrevious || !this.previousCursor) {
      return;
    }
    this.loadTransactions(undefined, this.previousCursor);
  }

  onPageSizeChange(newPageSize: number): void {
    if (newPageSize !== this.pageSize) {
      this.pageSize = newPageSize;
      this.loadTransactions();
    }
  }

  goBack(): void {
    this.router.navigate(['/manage-subscriptions']);
  }

  viewReceipt(url?: string): void {
    if (!url) {
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  downloadPdf(url?: string, invoiceNumber?: string): void {
    if (!url) {
      return;
    }
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.download = `${invoiceNumber || 'invoice'}.pdf`;
    link.click();
  }
}
